import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { blankFilter, buildListRequest, effectiveQueryKey } from './filterModel';
import type {
  ListColumnState,
  ListColumnWidthMode,
  ListExportColumn,
  ListFilterFieldDef,
  ListFilterValue,
  ListSortState,
  ResolvedListPageConfig,
} from './types';

export const LIST_PAGE_SIZES = [10, 20, 30, 50, 100] as const;
export const LIST_PAGE_SIZE_DEFAULT = 30;
export const PANEL_WIDTH_DEFAULT = 252;
/** Limit řádků exportu (posílá se na BE; nad limitem se tiše ořízne + FE toast). */
export const LIST_EXPORT_MAX_ROWS = 100_000;
const LIVE_DEBOUNCE_MS = 300;

/** Stav aplikovaný při otevření agendy (z centrální persistence) — viz anatomie 05 §6. */
export interface ListPageInitialState {
  /** Filtry naposledy aktivního pohledu (s domergovanými display items). */
  filters?: ListFilterValue[];
  activeViewId?: string;
  /** Spustit dotaz hned (pohled existuje / autoLoad); jinak stav „čeká na vyhledání". */
  searchOnInit: boolean;
  columns?: ListColumnState[];
  widthMode?: ListColumnWidthMode;
  /** Počáteční výběr řádků (picker režim — seed z aktuálně vybraných id). */
  selection?: (string | number)[];
  viewOrder?: string[];
  prefs?: {
    pageSize?: number;
    live?: boolean;
    panelOpen?: boolean;
    panelWidth?: number;
    stats?: boolean;
  };
}

export interface ListPageController<TItem> {
  // dotaz
  filters: ListFilterValue[];
  setFilters: (filters: ListFilterValue[]) => void;
  upsertFilter: (value: ListFilterValue) => void;
  removeFilter: (field: string) => void;
  /** ✕ na chipu: připnutý filtr vyprázdní, nepřipnutý odebere (anatomie 01 §3). */
  clearFilter: (field: string) => void;
  /** Hromadné Vymazat: připnuté vyprázdní, ostatní odebere. */
  clearAllFilters: () => void;
  togglePin: (field: string) => void;
  fulltext: string;
  setFulltext: (value: string) => void;
  fieldDef: (field: string) => ListFilterFieldDef | undefined;
  activeFilterCount: number;

  // vykonání
  live: boolean;
  setLive: (value: boolean) => void;
  /** Rozpracovaný dotaz se liší od odeslaného (žlutá tečka na Vyhledat). */
  pending: boolean;
  searched: boolean;
  search: () => void;

  // výsledek
  items: TItem[];
  totalCount: number;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;

  // řazení + stránkování
  sort: ListSortState;
  sortBy: (column: string) => void;
  setSort: (sort: ListSortState) => void;
  page: number;
  setPage: (page: number) => void;
  totalPages: number;
  pageSize: number;
  setPageSize: (size: number) => void;

  // výběr řádků
  selection: ReadonlySet<string | number>;
  toggleSelected: (key: string | number) => void;
  togglePageSelected: () => void;
  clearSelection: () => void;
  pageAllSelected: boolean;
  pageSomeSelected: boolean;

  // sloupce
  columnStates: ListColumnState[];
  setColumnStates: (states: ListColumnState[]) => void;
  toggleColumn: (key: string) => void;
  togglePinColumn: (key: string) => void;
  setColumnWidth: (key: string, width: number) => void;
  widthMode: ListColumnWidthMode;
  setWidthMode: (mode: ListColumnWidthMode) => void;

  // panel filtrů
  panelOpen: boolean;
  setPanelOpen: (open: boolean) => void;
  panelWidth: number;
  setPanelWidth: (width: number) => void;

  // statistiky (souhrnný pruh) — dotaz běží jen je-li pruh zobrazený a šablona má `api.stats`
  statsOpen: boolean;
  setStatsOpen: (open: boolean) => void;
  stats: unknown;
  statsLoading: boolean;

  // export — vrátí XLSX blob aktuálního (odeslaného) dotazu + zda se výsledek ořízl na limit
  exportList: (columns: ListExportColumn[]) => Promise<{ blob: Blob; trimmed: boolean } | null>;

  // náhledový drawer
  previewItem: TItem | null;
  openPreview: (item: TItem) => void;
  closePreview: () => void;
  /** Otevře záznam: náhledový drawer má přednost před `onOpen`. */
  openRecord: (item: TItem) => void;
  /** Je co otevřít (preview nebo onOpen) — řídí odkaz na identifikačním sloupci a akci řádku. */
  canOpen: boolean;

  // pohledy (stav drží controller; CRUD orchestruje useListPageViews)
  activeViewId: string;
  /** Označí pohled jako aktivní bez zásahu do filtrů (po uložení/duplikaci). */
  setActiveView: (viewId: string) => void;
  /** Pořadí záložek pohledů (uživatelská preference, drag & drop). */
  viewOrder: string[];
  setViewOrder: (order: string[]) => void;
  /**
   * Výběr pohledu (anatomie 01 §2): nahradí filtry kopií definice, vyčistí fulltext
   * a VŽDY spustí dotaz se skokem na stranu 1 — i při vypnutém Živě.
   */
  applyView: (viewId: string, filters: ListFilterValue[]) => void;
  /**
   * Tichá výměna display items selector filtru po background revalidaci —
   * nemění klíč dotazu, dirty stav ani stránku.
   */
  refreshFilterItems: (field: string, items: unknown[]) => void;

  rowKey: (item: TItem) => string | number;
  /** Otevření detailu záznamu (z akčního sloupce / identifikačního odkazu). */
  onOpen?: (item: TItem) => void;
}

/**
 * Stavový stroj ListPage — drží odděleně rozpracovaný a odeslaný dotaz (anatomie 02 §1),
 * režim Živě s debouncem, serverové stránkování přes React Query (poslední dotaz vyhrává
 * přes `signal`) a výběr řádků přežívající stránkování.
 */
export function useListPageController<TItem>(
  config: ResolvedListPageConfig<TItem>,
  initial?: ListPageInitialState
): ListPageController<TItem> {
  const defs = useMemo(
    () => new Map(config.filters.map((def) => [def.key, def])),
    [config.filters]
  );

  // ── rozpracovaný dotaz (init = naposledy aktivní pohled ze serveru) ──
  const [filters, setFilters] = useState<ListFilterValue[]>(
    () => initial?.filters?.map((f) => ({ ...f })) ?? []
  );
  const [fulltext, setFulltext] = useState('');
  const draftKey = useMemo(
    () => effectiveQueryKey(filters, fulltext, defs),
    [filters, fulltext, defs]
  );

  // ── odeslaný dotaz (null = čeká na vyhledání); init synchronně — žádný dvojitý fetch ──
  const [submitted, setSubmitted] = useState<{
    filters: ListFilterValue[];
    fulltext: string;
  } | null>(() => {
    const searchOnInit = initial ? initial.searchOnInit : config.autoLoad;
    return searchOnInit
      ? { filters: initial?.filters?.map((f) => ({ ...f })) ?? [], fulltext: '' }
      : null;
  });
  const submittedKey = useMemo(
    () => (submitted ? effectiveQueryKey(submitted.filters, submitted.fulltext, defs) : null),
    [submitted, defs]
  );

  const [live, setLive] = useState(initial?.prefs?.live ?? true);
  const [sort, setSortState] = useState<ListSortState>(config.defaultSort);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(initial?.prefs?.pageSize ?? LIST_PAGE_SIZE_DEFAULT);

  const search = useCallback(() => {
    setSubmitted({ filters: filters.map((f) => ({ ...f })), fulltext });
    setPage(1);
  }, [filters, fulltext]);

  const searchRef = useRef(search);
  searchRef.current = search;

  const pending = submitted !== null && submittedKey !== draftKey;

  // Živě: změna klíče efektivního dotazu spustí (debounced) vyhledání; prázdné filtry
  // a změny připnutí klíč nemění, takže zbytečné načtení nikdy nevyvolají.
  useEffect(() => {
    if (!live || submitted === null || submittedKey === draftKey) {
      return;
    }
    const timer = setTimeout(() => searchRef.current(), LIVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [live, draftKey, submittedKey, submitted]);

  const setLiveAndFlush = useCallback(
    (value: boolean) => {
      setLive(value);
      if (value && submitted !== null && submittedKey !== draftKey) {
        searchRef.current();
      }
    },
    [submitted, submittedKey, draftKey]
  );

  // ── sloupce ──
  const [columnStates, setColumnStates] = useState<ListColumnState[]>(
    () =>
      initial?.columns ??
      config.columns.map((c) => ({
        key: c.key,
        visible: c.visible !== false,
        pinned: Boolean(c.frozen),
        width: undefined,
      }))
  );
  const [widthMode, setWidthMode] = useState<ListColumnWidthMode>(initial?.widthMode ?? 'auto');

  const visibleColumnKeys = useMemo(
    () => columnStates.filter((c) => c.visible).map((c) => c.key),
    [columnStates]
  );

  // ── dotaz ──
  // Identitu dotazu nese KANONICKÁ serializace odeslaného dotazu (submittedKey, anatomie 02 §1):
  // `submitted.filters/fulltext` a `defs` jsou v klíči obsažené přes submittedKey, `config.api/key`
  // je stabilní per šablona. `visibleColumnKeys` je v klíči, protože BE drahé sloupce projektuje
  // jen pro viditelné sloupce (F3a) — změna viditelnosti drahého sloupce musí vyvolat refetch.
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const query = useQuery({
    queryKey: [config.key, 'list', submittedKey, sort, page, pageSize, visibleColumnKeys],
    enabled: submitted !== null,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) =>
      config.api.list(
        buildListRequest(
          submitted?.filters ?? [],
          submitted?.fulltext ?? '',
          sort,
          page,
          pageSize,
          visibleColumnKeys,
          defs
        ),
        signal
      ),
  });

  const [statsOpen, setStatsOpen] = useState(initial?.prefs?.stats ?? false);

  // Statistiky souhrnného pruhu — dotaz běží jen je-li pruh zobrazený (šetří dotaz). Závisí
  // jen na filtrech/fulltextu (submittedKey), ne na stránce/řazení/sloupcích (BE je u stats
  // ignoruje), proto nejsou v klíči.
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const statsQuery = useQuery({
    queryKey: [config.key, 'stats', submittedKey],
    enabled: submitted !== null && config.statsApi !== undefined && statsOpen,
    queryFn: ({ signal }) =>
      config.statsApi!(
        buildListRequest(
          submitted?.filters ?? [],
          submitted?.fulltext ?? '',
          sort,
          1,
          pageSize,
          visibleColumnKeys,
          defs
        ),
        signal
      ),
  });

  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const totalCount = query.data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // ── výběr řádků (přes id — přežívá stránkování, anatomie 03 §4) ──
  const [selection, setSelection] = useState<ReadonlySet<string | number>>(
    () => new Set(initial?.selection ?? [])
  );
  const [panelOpen, setPanelOpen] = useState(initial?.prefs?.panelOpen ?? false);
  const [panelWidth, setPanelWidth] = useState(initial?.prefs?.panelWidth ?? PANEL_WIDTH_DEFAULT);

  // ── pohledy ──
  const [activeViewId, setActiveViewId] = useState(initial?.activeViewId ?? '~all');
  const [viewOrder, setViewOrder] = useState<string[]>(initial?.viewOrder ?? []);

  const applyView = useCallback((viewId: string, viewFilters: ListFilterValue[]) => {
    const copy = viewFilters.map((f) => ({ ...f, ids: f.ids ? [...f.ids] : undefined }));
    setActiveViewId(viewId);
    setFilters(copy);
    setFulltext('');
    // Výběr pohledu je explicitní vyhledání — vždy, i při vypnutém Živě (anatomie 01 §2).
    setSubmitted({ filters: copy.map((f) => ({ ...f })), fulltext: '' });
    setPage(1);
  }, []);

  const refreshFilterItems = useCallback((field: string, items: unknown[]) => {
    setFilters((prev) => prev.map((f) => (f.field === field ? { ...f, items } : f)));
  }, []);
  const pageKeys = useMemo(() => items.map(config.rowKey), [items, config]);
  const pageAllSelected = pageKeys.length > 0 && pageKeys.every((key) => selection.has(key));
  const pageSomeSelected = pageKeys.some((key) => selection.has(key));

  // ── filtry: operace ──
  const upsertFilter = useCallback((value: ListFilterValue) => {
    setFilters((prev) => {
      const index = prev.findIndex((f) => f.field === value.field);
      if (index < 0) {
        return [...prev, value];
      }
      return prev.map((f, i) => (i === index ? value : f));
    });
  }, []);

  const togglePin = useCallback(
    (field: string) => {
      setFilters((prev) => {
        const index = prev.findIndex((f) => f.field === field);
        if (index < 0) {
          const def = defs.get(field);
          return def ? [...prev, { ...blankFilter(def), pinned: true }] : prev;
        }
        return prev.map((f, i) => (i === index ? { ...f, pinned: !f.pinned } : f));
      });
    },
    [defs]
  );

  const clearFilter = useCallback(
    (field: string) => {
      setFilters((prev) => {
        const existing = prev.find((f) => f.field === field);
        if (!existing) {
          return prev;
        }
        if (existing.pinned) {
          const def = defs.get(field);
          return def
            ? prev.map((f) => (f.field === field ? { ...blankFilter(def), pinned: true } : f))
            : prev;
        }
        return prev.filter((f) => f.field !== field);
      });
    },
    [defs]
  );

  const clearAllFilters = useCallback(() => {
    setFilters((prev) =>
      prev
        .filter((f) => f.pinned)
        .map((f) => {
          const def = defs.get(f.field);
          return def ? { ...blankFilter(def), pinned: true } : f;
        })
    );
  }, [defs]);

  const activeFilterCount = filters.length;

  // ── náhledový drawer ──
  const [previewItem, setPreviewItem] = useState<TItem | null>(null);
  const openPreview = useCallback((item: TItem) => setPreviewItem(item), []);
  const closePreview = useCallback(() => setPreviewItem(null), []);
  const openRecord = useCallback(
    (item: TItem) => {
      if (config.preview) {
        setPreviewItem(item);
      } else {
        config.onOpen?.(item);
      }
    },
    [config]
  );
  const canOpen = config.preview !== undefined || config.onOpen !== undefined;

  // Export odeslaného dotazu (ne rozpracovaného) — odpovídá tomu, co je v seznamu vidět.
  const exportList = useCallback(
    async (columns: ListExportColumn[]) => {
      if (!config.exportApi || submitted === null) {
        return null;
      }
      const blob = await config.exportApi(
        buildListRequest(
          submitted.filters,
          submitted.fulltext,
          sort,
          1,
          pageSize,
          visibleColumnKeys,
          defs
        ),
        columns,
        LIST_EXPORT_MAX_ROWS
      );
      return { blob, trimmed: totalCount > LIST_EXPORT_MAX_ROWS };
    },
    [config, submitted, sort, pageSize, visibleColumnKeys, defs, totalCount]
  );

  return {
    filters,
    setFilters,
    upsertFilter,
    removeFilter: useCallback(
      (field: string) => setFilters((prev) => prev.filter((f) => f.field !== field)),
      []
    ),
    clearFilter,
    clearAllFilters,
    togglePin,
    fulltext,
    setFulltext,
    fieldDef: useCallback((field: string) => defs.get(field), [defs]),
    activeFilterCount,

    live,
    setLive: setLiveAndFlush,
    pending: pending && !live,
    searched: submitted !== null,
    search,

    items,
    totalCount,
    isLoading: query.isFetching,
    isError: query.isError,
    refetch: () => void query.refetch(),

    sort,
    sortBy: useCallback(
      (column: string) =>
        setSortState((prev) =>
          prev.column === column ? { column, desc: !prev.desc } : { column, desc: false }
        ),
      []
    ),
    setSort: setSortState,
    page,
    setPage: useCallback((next: number) => setPage(Math.max(1, next)), []),
    totalPages,
    pageSize,
    setPageSize: useCallback((size: number) => {
      setPageSizeState(size);
      setPage(1);
    }, []),

    selection,
    toggleSelected: useCallback((key: string | number) => {
      setSelection((prev) => {
        const next = new Set(prev);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      });
    }, []),
    togglePageSelected: useCallback(() => {
      setSelection((prev) => {
        const next = new Set(prev);
        if (pageKeys.length > 0 && pageKeys.every((key) => next.has(key))) {
          pageKeys.forEach((key) => next.delete(key));
        } else {
          pageKeys.forEach((key) => next.add(key));
        }
        return next;
      });
    }, [pageKeys]),
    clearSelection: useCallback(() => setSelection(new Set()), []),
    pageAllSelected,
    pageSomeSelected,

    columnStates,
    setColumnStates,
    toggleColumn: useCallback(
      (key: string) =>
        setColumnStates((prev) =>
          prev.map((c) => (c.key === key && !c.pinned ? { ...c, visible: !c.visible } : c))
        ),
      []
    ),
    togglePinColumn: useCallback(
      (key: string) =>
        setColumnStates((prev) =>
          prev.map((c) =>
            c.key === key ? { ...c, pinned: !c.pinned, visible: c.pinned ? c.visible : true } : c
          )
        ),
      []
    ),
    setColumnWidth: useCallback(
      (key: string, width: number) =>
        setColumnStates((prev) => prev.map((c) => (c.key === key ? { ...c, width } : c))),
      []
    ),
    widthMode,
    setWidthMode,

    panelOpen,
    setPanelOpen,
    panelWidth,
    setPanelWidth,

    statsOpen,
    setStatsOpen,
    stats: statsQuery.data,
    statsLoading: statsQuery.isFetching,

    exportList,

    previewItem,
    openPreview,
    closePreview,
    openRecord,
    canOpen,

    activeViewId,
    setActiveView: setActiveViewId,
    viewOrder,
    setViewOrder,
    applyView,
    refreshFilterItems,

    rowKey: config.rowKey,
    onOpen: config.onOpen,
  };
}
